'use client';

/**
 * @packageDocumentation
 * @category Component
 */

import 'BomdisoftComponents/notifications/snackbars/styles.css';
import { CheckCircleOutlineOutlinedIcon, CloseRoundedIcon, ErrorOutlineOutlinedIcon, InfoOutlinedIcon, ReportProblemOutlinedIcon } from 'BomdisoftComponents/icons/index';
import { toSnackAttributes } from 'BomdisoftComponents/notifications/snackbars/render';
import type { SnackbarItem, SnackbarVariant } from 'BomdisoftComponents/notifications/snackbars/store';
import { Tooltip } from 'BomdisoftComponents/tooltip/index';
import { closeNotificationT, getLanguageFromLocale } from '@bomdisoft/translations';
import { useParams } from 'next/navigation';
import { type ReactNode, useEffect } from 'react';

export type SnackProps = {
  item: SnackbarItem;
  onClose: (id: number) => void;
};

const VARIANT_ICONS: Record<SnackbarVariant, ReactNode> = {
  default: null,
  error: <ErrorOutlineOutlinedIcon size='inherit' />,
  info: <InfoOutlinedIcon size='inherit' />,
  success: <CheckCircleOutlineOutlinedIcon size='inherit' />,
  warning: <ReportProblemOutlinedIcon size='inherit' />,
};

const Marker = ({ item }: { item: SnackbarItem }) => {
  if (item.indicator === 'dot') return <span aria-hidden='true' className='bomdisoft-snack-dot' />;
  if (item.indicator !== 'icon') return null;
  const glyph = VARIANT_ICONS[item.variant];
  return glyph === null ? null : (
    <span aria-hidden='true' className='bomdisoft-snack-icon'>
      {glyph}
    </span>
  );
};

export const Snack = ({ item, onClose }: SnackProps) => {
  const params = useParams();
  const language = getLanguageFromLocale(typeof params?.lang === 'string' ? params.lang : undefined);
  useEffect(() => {
    if (item.autoHideDuration === null) return;
    const timer = setTimeout(() => onClose(item.id), item.autoHideDuration);
    return () => clearTimeout(timer);
  }, [item.autoHideDuration, item.id, onClose]);

  const handleAction = () => {
    item.customAction?.();
    onClose(item.id);
  };

  // An error interrupts; everything else waits its turn. role='status' and
  // role='alert' carry politeness on their own, which is why the region no longer
  // declares aria-live: the two together announced every toast twice.
  return (
    <div className='bomdisoft-snack' {...toSnackAttributes(item)}>
      <Marker item={item} />
      <span className='bomdisoft-snack-message'>
        <span className='bomdisoft-snack-title'>{item.message}</span>
        {item.description === undefined ? null : <span className='bomdisoft-snack-description'>{item.description}</span>}
      </span>
      <Tooltip title={closeNotificationT[language]}>
        <button aria-label={closeNotificationT[language]} className='bomdisoft-snack-close' data-testid='snackbar-close' onClick={() => onClose(item.id)} type='button'>
          <CloseRoundedIcon size={16} />
        </button>
      </Tooltip>
      {item.customAction && item.customActionText ? (
        <span className='bomdisoft-snack-actions'>
          <button className='bomdisoft-snack-action' data-testid='snackbar-action' onClick={handleAction} type='button'>
            {item.customActionText}
          </button>
        </span>
      ) : null}
    </div>
  );
};
